import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { paths } from '../../common/constants/pathVariables';
import { SubscriptionCategory } from '../../common/enums';
import { fetchSubscription } from '../../common/fetch';
import { formatPhoneNumber } from '../../common/utils/phoneNumberUtils';
import { addMdmIdToUrl } from '../../common/utils/urlUtils';
import { CustomerOrderProductType } from '../../generated/api/customerOrderProductType';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import type { ChangeRequestDetails } from '../../generated/api/changeRequestDetails';

interface ChangeRequestInfoLinkProps {
  changeRequest: ChangeRequestDetails;
  mdmId?: string;
}

const getChangeTargetLinkBasePath = (productType: CustomerOrderProductType): string => {
  switch (productType) {
    case CustomerOrderProductType.DEVICE:
      return paths.PS_DEVICES;
    case CustomerOrderProductType.VOICE:
      return paths.PS_MOBILE_SUBSCRIPTIONS;
    case CustomerOrderProductType.BROADBAND:
      return paths.PS_BROADBAND_SUBSCRIPTIONS;
    case CustomerOrderProductType.SERVICE:
      return paths.PS_SERVICES;
    case CustomerOrderProductType.EPP:
      return paths.PS_DEVICES;
    default:
      throw new Error('Unsupported product type for change request target link');
  }
};

const getSubscriptionCategoryFromProductType = (productType: CustomerOrderProductType): SubscriptionCategory => {
  switch (productType) {
    case CustomerOrderProductType.DEVICE:
      return SubscriptionCategory.DEVICE;
    case CustomerOrderProductType.VOICE:
      return SubscriptionCategory.VOICE;
    case CustomerOrderProductType.BROADBAND:
      return SubscriptionCategory.BROADBAND;
    case CustomerOrderProductType.SERVICE:
      return SubscriptionCategory.SERVICE;
    case CustomerOrderProductType.EPP:
      return SubscriptionCategory.DEVICE;
    default:
      throw new Error('Unsupported product type for subscription category mapping');
  }
};

export const ChangeRequestInfoLink = ({ changeRequest, mdmId }: ChangeRequestInfoLinkProps) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusType | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        if (
          changeRequest.subscriptionOrDeviceLink &&
          changeRequest.productType &&
          changeRequest.productType !== CustomerOrderProductType.OTHER
        ) {
          const category = getSubscriptionCategoryFromProductType(changeRequest.productType);
          const subscription = await fetchSubscription(category, changeRequest.subscriptionOrDeviceLink, mdmId || '');
          setSubscriptionStatus(subscription.subscriptionStatus);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatus();
  }, [changeRequest.subscriptionOrDeviceLink, changeRequest.productType, mdmId]);

  const linkText =
    changeRequest.productType === CustomerOrderProductType.VOICE && changeRequest.subscriptionOrDeviceInfo
      ? formatPhoneNumber(changeRequest.subscriptionOrDeviceInfo)
      : changeRequest.subscriptionOrDeviceLink;

  if (isLoading || subscriptionStatus !== SubscriptionStatusType.ACTIVE) {
    return <>{linkText}</>;
  }

  const basePath = getChangeTargetLinkBasePath(changeRequest.productType!);
  const linkPath = mdmId ? addMdmIdToUrl(`${basePath}/${changeRequest.subscriptionOrDeviceLink}`, mdmId) : basePath;

  return <Link to={linkPath}>{linkText}</Link>;
};
